import { t } from "ttag";

import {
  BOOLEAN,
  COORDINATE,
  FOREIGN_KEY,
  LOCATION,
  NUMBER,
  PRIMARY_KEY,
  STRING,
  STRING_LIKE,
  TEMPORAL,
  TYPE,
  UNKNOWN,
} from "metabase-lib/v1/types/constants";
import {
  isBoolean,
  isDate,
  isLongitude,
  isNumeric,
} from "metabase-lib/v1/types/utils/isa";
import type { Field, ParameterOptions, Table } from "metabase-types/api";

type FilterArgument =
  | { type: "text" }
  | { type: "number" }
  | { type: "date" }
  | { type: "hidden"; default: Field["id"] }
  | {
      type: "select";
      values: { key: boolean | Field["id"]; name: string }[];
      default?: boolean;
    };

export type FilterArgumentFormatOptions = {
  hide?: boolean;
  compact?: boolean;
  column?: { semantic_type: string };
};

export type FieldFilterOperator = {
  validArgumentsFilters: ((field: Field, table: Table) => FilterArgument)[];
  multi?: boolean;
  placeholders?: string[];
  formatOptions?: FilterArgumentFormatOptions[];
  options?: Record<string, { defaultValue: boolean }>;
  optionsDefaults?: ParameterOptions;
};

export type NamedFilterOperator = {
  name: string;
  readonly verboseName: string;
  multi?: boolean;
};

function freeformArgument(_field: Field, _table: Table): FilterArgument {
  return {
    type: "text",
  };
}

function numberArgument(_field: Field, _table: Table): FilterArgument {
  return {
    type: "number",
  };
}

function comparableArgument(field: Field, _table: Table): FilterArgument {
  if (isDate(field)) {
    return {
      type: "date",
    };
  }

  if (isNumeric(field)) {
    return {
      type: "number",
    };
  }

  return {
    type: "text",
  };
}

function equivalentArgument(field: Field, _table: Table): FilterArgument {
  if (isBoolean(field)) {
    return {
      type: "select",
      values: [
        { key: true, name: t`True` },
        { key: false, name: t`False` },
      ],
      default: true,
    };
  }

  if (isDate(field)) {
    return {
      type: "date",
    };
  }

  if (isNumeric(field)) {
    return {
      type: "number",
    };
  }

  return {
    type: "text",
  };
}

function longitudeFieldSelectArgument(
  _field: Field,
  table: Table,
): FilterArgument {
  const values = (table.fields ?? [])
    .filter((field) => isLongitude(field))
    .map((field) => ({
      key: field.id,
      name: field.display_name,
    }));

  if (values.length === 1) {
    return {
      type: "hidden",
      default: values[0].key,
    };
  }

  return {
    type: "select",
    values,
  };
}

const CASE_SENSITIVE_OPTION = {
  "case-sensitive": {
    defaultValue: true,
  },
};

// each of these has an implicit field argument, followed by 0 or more additional arguments
export const FIELD_FILTER_OPERATORS = {
  "=": {
    validArgumentsFilters: [equivalentArgument],
    multi: true,
  },
  "!=": {
    validArgumentsFilters: [equivalentArgument],
    multi: true,
  },
  "is-empty": {
    validArgumentsFilters: [],
  },
  "not-empty": {
    validArgumentsFilters: [],
  },
  "is-null": {
    validArgumentsFilters: [],
  },
  "not-null": {
    validArgumentsFilters: [],
  },
  "<": {
    validArgumentsFilters: [comparableArgument],
  },
  "<=": {
    validArgumentsFilters: [comparableArgument],
  },
  ">": {
    validArgumentsFilters: [comparableArgument],
  },
  ">=": {
    validArgumentsFilters: [comparableArgument],
  },
  inside: {
    validArgumentsFilters: [
      longitudeFieldSelectArgument,
      numberArgument,
      numberArgument,
      numberArgument,
      numberArgument,
    ],
    get placeholders() {
      return [
        t`Select longitude field`,
        t`Enter upper latitude`,
        t`Enter left longitude`,
        t`Enter lower latitude`,
        t`Enter right longitude`,
      ];
    },
    formatOptions: [
      { hide: true },
      { column: { semantic_type: TYPE.Latitude }, compact: true },
      { column: { semantic_type: TYPE.Longitude }, compact: true },
      { column: { semantic_type: TYPE.Latitude }, compact: true },
      { column: { semantic_type: TYPE.Longitude }, compact: true },
    ],
  },
  between: {
    validArgumentsFilters: [comparableArgument, comparableArgument],
  },
  "starts-with": {
    multi: true,
    validArgumentsFilters: [freeformArgument],
    options: CASE_SENSITIVE_OPTION,
    optionsDefaults: { "case-sensitive": false },
  },
  "ends-with": {
    multi: true,
    validArgumentsFilters: [freeformArgument],
    options: CASE_SENSITIVE_OPTION,
    optionsDefaults: { "case-sensitive": false },
  },
  contains: {
    multi: true,
    validArgumentsFilters: [freeformArgument],
    options: CASE_SENSITIVE_OPTION,
    optionsDefaults: { "case-sensitive": false },
  },
  "does-not-contain": {
    multi: true,
    validArgumentsFilters: [freeformArgument],
    options: CASE_SENSITIVE_OPTION,
    optionsDefaults: { "case-sensitive": false },
  },
} satisfies Record<string, FieldFilterOperator>;

export type FilterOperatorName = keyof typeof FIELD_FILTER_OPERATORS;

const DEFAULT_FILTER_OPERATORS: NamedFilterOperator[] = [
  {
    name: "=",
    get verboseName() {
      return t`Is`;
    },
  },
  {
    name: "!=",
    get verboseName() {
      return t`Is not`;
    },
  },
  {
    name: "is-null",
    get verboseName() {
      return t`Is empty`;
    },
  },
  {
    name: "not-null",
    get verboseName() {
      return t`Not empty`;
    },
  },
];

const KEY_FILTER_OPERATORS: NamedFilterOperator[] = [
  {
    name: "=",
    get verboseName() {
      return t`Is`;
    },
  },
  {
    name: "!=",
    get verboseName() {
      return t`Is not`;
    },
  },
  {
    name: ">",
    get verboseName() {
      return t`Greater than`;
    },
  },
  {
    name: "<",
    get verboseName() {
      return t`Less than`;
    },
  },
  {
    name: "between",
    get verboseName() {
      return t`Between`;
    },
  },
  {
    name: ">=",
    get verboseName() {
      return t`Greater than or equal to`;
    },
  },
  {
    name: "<=",
    get verboseName() {
      return t`Less than or equal to`;
    },
  },
  {
    name: "is-null",
    get verboseName() {
      return t`Is empty`;
    },
  },
  {
    name: "not-null",
    get verboseName() {
      return t`Not empty`;
    },
  },
];

// ordered list of operators and metadata per type
export const FILTER_OPERATORS_BY_TYPE_ORDERED = {
  [NUMBER]: [
    {
      name: "=",
      get verboseName() {
        return t`Equal to`;
      },
    },
    {
      name: "!=",
      get verboseName() {
        return t`Not equal to`;
      },
    },
    {
      name: ">",
      get verboseName() {
        return t`Greater than`;
      },
    },
    {
      name: "<",
      get verboseName() {
        return t`Less than`;
      },
    },
    {
      name: "between",
      get verboseName() {
        return t`Between`;
      },
    },
    {
      name: ">=",
      get verboseName() {
        return t`Greater than or equal to`;
      },
    },
    {
      name: "<=",
      get verboseName() {
        return t`Less than or equal to`;
      },
    },
    {
      name: "is-null",
      get verboseName() {
        return t`Is empty`;
      },
    },
    {
      name: "not-null",
      get verboseName() {
        return t`Not empty`;
      },
    },
  ],
  [STRING]: [
    {
      name: "=",
      get verboseName() {
        return t`Is`;
      },
    },
    {
      name: "!=",
      get verboseName() {
        return t`Is not`;
      },
    },
    {
      name: "contains",
      get verboseName() {
        return t`Contains`;
      },
    },
    {
      name: "does-not-contain",
      get verboseName() {
        return t`Does not contain`;
      },
    },
    {
      name: "is-null",
      get verboseName() {
        return t`Is null`;
      },
    },
    {
      name: "not-null",
      get verboseName() {
        return t`Not null`;
      },
    },
    {
      name: "is-empty",
      get verboseName() {
        return t`Is empty`;
      },
    },
    {
      name: "not-empty",
      get verboseName() {
        return t`Not empty`;
      },
    },
    {
      name: "starts-with",
      get verboseName() {
        return t`Starts with`;
      },
    },
    {
      name: "ends-with",
      get verboseName() {
        return t`Ends with`;
      },
    },
  ],
  [STRING_LIKE]: [
    {
      name: "=",
      get verboseName() {
        return t`Is`;
      },
    },
    {
      name: "!=",
      get verboseName() {
        return t`Is not`;
      },
    },
    {
      name: "is-null",
      get verboseName() {
        return t`Is null`;
      },
    },
    {
      name: "not-null",
      get verboseName() {
        return t`Not null`;
      },
    },
    {
      name: "is-empty",
      get verboseName() {
        return t`Is empty`;
      },
    },
    {
      name: "not-empty",
      get verboseName() {
        return t`Not empty`;
      },
    },
  ],
  [TEMPORAL]: [
    {
      name: "!=",
      get verboseName() {
        return t`Excludes`;
      },
    },
    {
      name: "=",
      get verboseName() {
        return t`Is`;
      },
    },
    {
      name: "<",
      get verboseName() {
        return t`Before`;
      },
    },
    {
      name: ">",
      get verboseName() {
        return t`After`;
      },
    },
    {
      name: "between",
      get verboseName() {
        return t`Between`;
      },
    },
    {
      name: "is-null",
      get verboseName() {
        return t`Is empty`;
      },
    },
    {
      name: "not-null",
      get verboseName() {
        return t`Is not empty`;
      },
    },
  ],
  [LOCATION]: [
    {
      name: "=",
      get verboseName() {
        return t`Is`;
      },
    },
    {
      name: "!=",
      get verboseName() {
        return t`Is not`;
      },
    },
    {
      name: "is-null",
      get verboseName() {
        return t`Is empty`;
      },
    },
    {
      name: "not-null",
      get verboseName() {
        return t`Not empty`;
      },
    },
    {
      name: "contains",
      get verboseName() {
        return t`Contains`;
      },
    },
    {
      name: "does-not-contain",
      get verboseName() {
        return t`Does not contain`;
      },
    },
    {
      name: "starts-with",
      get verboseName() {
        return t`Starts with`;
      },
    },
    {
      name: "ends-with",
      get verboseName() {
        return t`Ends with`;
      },
    },
  ],
  [COORDINATE]: [
    {
      name: "=",
      get verboseName() {
        return t`Is`;
      },
    },
    {
      name: "!=",
      get verboseName() {
        return t`Is not`;
      },
    },
    {
      name: "inside",
      get verboseName() {
        return t`Inside`;
      },
    },
    {
      name: ">",
      get verboseName() {
        return t`Greater than`;
      },
    },
    {
      name: "<",
      get verboseName() {
        return t`Less than`;
      },
    },
    {
      name: "between",
      get verboseName() {
        return t`Between`;
      },
    },
    {
      name: ">=",
      get verboseName() {
        return t`Greater than or equal to`;
      },
    },
    {
      name: "<=",
      get verboseName() {
        return t`Less than or equal to`;
      },
    },
  ],
  [BOOLEAN]: [
    {
      name: "=",
      get verboseName() {
        return t`Is`;
      },
      multi: false,
    },
    {
      name: "is-null",
      get verboseName() {
        return t`Is empty`;
      },
    },
    {
      name: "not-null",
      get verboseName() {
        return t`Not empty`;
      },
    },
  ],
  [FOREIGN_KEY]: KEY_FILTER_OPERATORS,
  [PRIMARY_KEY]: KEY_FILTER_OPERATORS,
  [UNKNOWN]: DEFAULT_FILTER_OPERATORS,
} satisfies Record<string, NamedFilterOperator[]>;

export type FilterOperatorType = keyof typeof FILTER_OPERATORS_BY_TYPE_ORDERED;
